import { Check, CircleDashed, CircleDot } from 'lucide-react';
import type { ReactNode } from 'react';

import { FORM_SECTIONS, sectionState, type ProjectFormValues } from '../../schemas/project.schema';
import { messages } from '@/shared/constants/messages';
import { cn } from '@/shared/lib/utils';

interface SectionIndexProps {
  values: ProjectFormValues;
  /** Seção em que a rolagem está, vinda do scroll spy. */
  activeId: string | null;
}

interface SectionIndexColumnProps extends SectionIndexProps {
  /**
   * O pedido do revisor, abaixo do índice (decisão 45) — dentro da MESMA
   * coluna fixa, para acompanhar a rolagem: ele precisa estar legível enquanto
   * a pessoa mexe na seção 4, não só quando ela está no topo. O contêiner já
   * tem `max-h`/`overflow-y-auto`, então pedido longo rola dentro da coluna em
   * vez de estourar a tela.
   */
  reviewerRequest?: ReactNode;
}

/**
 * O índice do documento — e a resposta da spec para "indicação clara de
 * progresso" (US-15) agora que não há etapas (decisão 21).
 *
 * Progresso aqui é **quanto do documento está pronto**, não em que passo você
 * está: cada seção mostra se está completa, pela metade ou vazia, e clicar
 * leva até ela. É o que substitui o "3 de 5" do assistente.
 *
 * Em `lg+` ele é uma coluna fixa ao lado do documento; abaixo disso vira uma
 * linha de progresso presa ao topo (`SectionProgressBar`), porque em 375px
 * cada pixel vertical é campo de formulário e uma lista de âncoras compete
 * com o que a pessoa veio fazer.
 */
export function SectionIndex({ values, activeId, reviewerRequest }: SectionIndexColumnProps) {
  const required = FORM_SECTIONS.filter((section) => section.requiredFields.length > 0);
  const done = required.filter((section) => sectionState(section, values) === 'complete').length;

  return (
    // A coluna fixa, e não só o `<nav>`: o pedido do revisor mora nela e precisa
    // acompanhar a rolagem junto com o índice. Ele fica FORA do `<nav>` porque
    // não é navegação — um `<section>` com título dentro de um marco rotulado
    // "Seções do formulário" mentiria para quem navega por marcos.
    <div className="sticky top-20 hidden max-h-[calc(100vh-6rem)] overflow-y-auto lg:block">
      <nav aria-label="Seções do formulário">
        <ol className="space-y-1">
          {FORM_SECTIONS.map((section) => {
            const state = sectionState(section, values);
            const isActive = activeId === section.id;

            return (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'flex items-start gap-2 rounded-md px-2 py-1.5 text-sm',
                    'transition-[color,background-color,transform] duration-base ease-out',
                    // O item ativo avança 2px enquanto a rolagem passa por ele.
                    // Num índice de cinco linhas, só a cor faz a marca de "você
                    // está aqui" competir com a marca de "seção completa".
                    isActive && 'translate-x-0.5',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                    isActive
                      ? 'bg-primary/10 font-medium text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  <SectionMark state={state} optional={section.optional} />
                  <span className="min-w-0">
                    <span className="block leading-tight">
                      {section.number}. {section.title}
                    </span>
                    {section.optional ? (
                      <span className="text-xs text-muted-foreground">
                        {messages.form.optional}
                      </span>
                    ) : null}
                  </span>
                </a>
              </li>
            );
          })}
        </ol>

        <p className="mt-4 border-t border-border px-2 pt-3 text-xs text-muted-foreground">
          {messages.form.progress(done, required.length)}
        </p>
      </nav>

      {reviewerRequest ? <div className="mt-4">{reviewerRequest}</div> : null}
    </div>
  );
}

function SectionMark({
  state,
  optional,
}: {
  state: 'empty' | 'partial' | 'complete';
  optional?: boolean;
}) {
  const label =
    state === 'complete'
      ? messages.form.sectionComplete
      : state === 'partial'
        ? messages.form.sectionMissing
        : messages.form.sectionEmpty;

  const Icon = state === 'complete' ? Check : state === 'partial' ? CircleDot : CircleDashed;

  return (
    <>
      {/* `key={state}` remonta a marca a cada mudança, e só a de COMPLETA
          ganha o `pop`. É a recompensa de fechar uma seção — e é deliberado
          que "pela metade" não comemore nada: o formulário avisa que faltou,
          não celebra o inacabado. */}
      <Icon
        key={state}
        aria-hidden="true"
        className={cn(
          'mt-0.5 size-4 shrink-0',
          state === 'complete' && 'animate-pop',
          state === 'complete' && 'text-emerald-600',
          // Seção opcional pela metade não é pendência: ela não deve gritar
          // amarelo num formulário que declara a própria seção como opcional.
          state === 'partial' && !optional && 'text-amber-600',
          state === 'partial' && optional && 'text-muted-foreground',
          state === 'empty' && 'text-muted-foreground/60',
        )}
      />
      <span className="sr-only">{label}:</span>
    </>
  );
}

/** A mesma informação em uma linha, para telas estreitas. */
export function SectionProgressBar({ values, activeId }: SectionIndexProps) {
  const required = FORM_SECTIONS.filter((section) => section.requiredFields.length > 0);
  const done = required.filter((section) => sectionState(section, values) === 'complete').length;
  const active = FORM_SECTIONS.find((section) => section.id === activeId) ?? FORM_SECTIONS[0];
  const percent = Math.round((done / required.length) * 100);

  return (
    <div className="sticky top-14 z-20 -mx-4 border-b border-border bg-card/95 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6 lg:hidden">
      <div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
        <span>{messages.form.progress(done, required.length)}</span>
        {/* O nome da seção troca em fade conforme a rolagem passa de uma para
            a outra — trocar seco, em 375px, lê como a tela tendo um espasmo. */}
        <span key={active?.id} className="animate-fade-in truncate font-medium text-foreground">
          {active?.number}. {active?.title}
        </span>
      </div>
      <div
        className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={required.length}
        aria-valuenow={done}
        aria-label="Seções obrigatórias completas"
      >
        {/* `transition-[width]` e não `transition-all`: `all` faz o navegador
            observar toda propriedade animável da barra, inclusive as que a
            classe do pai muda, e produz interpolação onde ninguém pediu. */}
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-slow ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
